/**
 * SKETCH PLACEHOLDERS
 * ===================
 *
 * Every hand-drawn mark in the sport screens goes through this file, so the
 * real artwork can be dropped in later without touching a single layout file.
 *
 * These are PLACEHOLDERS. They are deliberately plain — geometric paths with a
 * little wobble — and are meant to be replaced by properly drawn SVGs. See
 * SKETCH.md in this folder for the list and what each one has to satisfy.
 *
 * Rules for a replacement:
 *  - keep the exported component name and its props;
 *  - use `stroke="currentColor"` and `fill="none"` so the theme drives colour;
 *  - keep the viewBox, or update the `preserveAspectRatio` note in SKETCH.md.
 */

import React from 'react';

/** Shared defaults — one place to tune the "pen". */
const PEN = {
  fill: 'none',
  stroke: 'currentColor',
  strokeLinecap: 'round',
  strokeLinejoin: 'round',
};

/**
 * PLACEHOLDER — wobbly rectangle used as a frame around a panel.
 * Stretches to its container, so the stroke is drawn in user units and the
 * `vector-effect` keeps it an even weight however far it is scaled.
 *
 * @param {string} [className]
 */
export function SketchFrame({ className = '' }) {
  return (
    <svg
      viewBox="0 0 100 60"
      preserveAspectRatio="none"
      aria-hidden="true"
      className={`pointer-events-none absolute inset-0 h-full w-full ${className}`}
    >
      <path
        {...PEN}
        vectorEffect="non-scaling-stroke"
        strokeWidth="1.2"
        d="M3 5 Q 50 2, 97 4 Q 99 30, 97 56 Q 50 59, 3 57 Q 1 30, 3 5 Z"
      />
    </svg>
  );
}

/**
 * PLACEHOLDER — squiggly underline, sized to the text above it.
 * @param {string} [className]
 */
export function SketchUnderline({ className = '' }) {
  return (
    <svg
      viewBox="0 0 100 5"
      preserveAspectRatio="none"
      aria-hidden="true"
      className={`pointer-events-none block h-[5px] w-full ${className}`}
    >
      <path
        {...PEN}
        vectorEffect="non-scaling-stroke"
        strokeWidth="1.6"
        d="M1 3 Q 25 0.5, 50 2.6 T 99 2"
      />
    </svg>
  );
}

/**
 * PLACEHOLDER — hand-drawn checkbox. The tick is only painted when checked.
 * @param {boolean} checked
 * @param {string} [className]
 */
export function SketchCheckbox({ checked, className = '' }) {
  return (
    <svg viewBox="0 0 20 20" aria-hidden="true" className={`h-5 w-5 shrink-0 ${className}`}>
      {/* The box: four strokes that do not quite meet at the corners */}
      <path {...PEN} strokeWidth="1.4" d="M3 4 Q 10 2.6, 17 3.6 Q 18 10, 17 16.6 Q 10 18, 3 16.8 Q 2 10, 3 4" />
      {checked && <path {...PEN} strokeWidth="2" d="M5.5 10.5 L 9 14 L 15 5.5" />}
    </svg>
  );
}

/**
 * PLACEHOLDER — wavy divider, a torn edge between two blocks.
 * @param {string} [className]
 */
export function SketchDivider({ className = '' }) {
  return (
    <svg
      viewBox="0 0 100 4"
      preserveAspectRatio="none"
      aria-hidden="true"
      className={`pointer-events-none block h-1 w-full ${className}`}
    >
      <path
        {...PEN}
        vectorEffect="non-scaling-stroke"
        strokeWidth="1.1"
        strokeDasharray="4 3"
        d="M0 2 Q 25 0.8, 50 2.2 T 100 1.6"
      />
    </svg>
  );
}

/**
 * PLACEHOLDER — dumbbell, marks the multi-station workout type.
 * @param {string} [className]
 */
export function SketchDumbbell({ className = '' }) {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" className={`h-4 w-4 ${className}`}>
      <path {...PEN} strokeWidth="1.5" d="M7 9.5 L 7 14.5 M4 8 L 4 16 M17 9.5 L 17 14.5 M20 8 L 20 16 M7 12 Q 12 11.4, 17 12" />
    </svg>
  );
}

/**
 * PLACEHOLDER — running figure, marks the one-step activity type.
 * @param {string} [className]
 */
export function SketchRunner({ className = '' }) {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" className={`h-4 w-4 ${className}`}>
      <circle {...PEN} strokeWidth="1.5" cx="14.5" cy="4.5" r="2" />
      <path {...PEN} strokeWidth="1.5" d="M13 8 L 9.5 12 L 12 15 L 10.5 21 M12 15 L 16 17 L 17 21 M13 9.5 L 18 10.5 M9.5 12 L 5 11" />
    </svg>
  );
}

/**
 * PLACEHOLDER — chart axes: two strokes that were not drawn with a ruler.
 * @param {string} [className]
 */
export function SketchAxes({ className = '' }) {
  return (
    <svg
      viewBox="0 0 100 60"
      preserveAspectRatio="none"
      aria-hidden="true"
      className={`pointer-events-none absolute inset-0 h-full w-full ${className}`}
    >
      <path
        {...PEN}
        vectorEffect="non-scaling-stroke"
        strokeWidth="1.2"
        d="M2 1 Q 3 30, 2.4 58 Q 50 59, 98 58.2"
      />
    </svg>
  );
}

/**
 * PLACEHOLDER — empty-state doodle for a chart with nothing in it yet.
 * @param {string} [className]
 */
export function SketchEmptyChart({ className = '' }) {
  return (
    <svg viewBox="0 0 120 60" aria-hidden="true" className={`h-16 w-32 ${className}`}>
      <path {...PEN} strokeWidth="1.2" strokeDasharray="5 4" d="M8 6 Q 9 30, 8 52 Q 60 53, 112 52" />
      <path {...PEN} strokeWidth="1.6" strokeDasharray="6 5" d="M16 44 Q 40 40, 58 30 T 104 14" opacity="0.5" />
    </svg>
  );
}
